import { useState, useEffect, useRef, useCallback } from 'react';
import { usePost } from 'hooks';
import { activitySearch } from 'services/api';
import { avoidChars } from 'models/searchErrorKeys';

/**
 * Safely escapes a search term for Elasticsearch by wrapping it in quotes
 * if it contains special characters
 *
 * @param {String} searchTerm - The term to escape
 * @returns {String} - The safely escaped term
 */
const escapeElasticSearchTerm = searchTerm => {
  if (!searchTerm) return searchTerm;
  const hasSpecialChars = avoidChars.some(char => searchTerm.includes(char));
  if (hasSpecialChars) {
    const escapedTerm = searchTerm.replace(/"/g, '"');
    return `"${escapedTerm}"`;
  }
  return searchTerm;
};

/**
 * Custom hook to handle activity search and processing
 *
 * @param {Number} researcherId - ID of the researcher
 * @param {String} searchId - ID for search requests
 * @param {Boolean} excludeOldWorkItems - Whether to filter out older work items
 * @returns {Object} - Methods and state for activity search
 */
export function useActivitySearch(researcherId, searchId, excludeOldWorkItems) {
  // State for search
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // State for data loading status
  const [hasActivities, setHasActivities] = useState(false);

  // References for debounce
  const searchTimeoutRef = useRef(null);

  // Create reusable search params function
  const getSearchParams = useCallback(
    query => ({
      query,
      id: searchId,
      count: 100,
      from: 0,
      order: 'Date',
      orderBy: 'desc',
      filters: {},
      nestedQueries: [],
    }),
    [searchId]
  );

  // Build the base query for researcher
  const buildBaseQuery = useCallback(() => {
    return `SciLeadsSuperResearcherId:(${researcherId})`;
  }, [researcherId]);

  // Build filtered query for recent items
  const buildFilteredQuery = useCallback(() => {
    return `${buildBaseQuery()} AND Date:[now/d-1y TO *]`;
  }, [buildBaseQuery]);

  // Build search query with optional date filter
  const buildSearchQuery = useCallback(
    (term, includeFilter) => {
      let query = buildBaseQuery();
      if (includeFilter) {
        query += ' AND Date:[now/d-1y TO *]';
      }
      if (term && term.trim()) {
        const safeTerm = escapeElasticSearchTerm(term.trim());
        query += ` AND ${safeTerm}`;
      }
      return query;
    },
    [buildBaseQuery]
  );

  // INITIAL LOAD: Fetch ALL work items (no filter)
  const [{ res: allItems, loading: allItemsLoading }] = usePost({
    url: activitySearch,
    onMount: true,
    body: getSearchParams(buildBaseQuery()),
  });

  // INITIAL LOAD: Fetch RECENT work items (past year)
  const [{ res: recentItems, loading: recentItemsLoading }] = usePost({
    url: activitySearch,
    onMount: true,
    body: getSearchParams(buildFilteredQuery()),
  });

  // SEARCH: Fetch items matching search term
  const [
    { res: searchResults, loading: searchResultsLoading },
    fetchSearchResults,
  ] = usePost({
    url: activitySearch,
    onMount: false,
  });

  // Update hasActivities when we have data
  useEffect(() => {
    if (allItems) {
      setHasActivities(!!allItems?.groupedResults?.length);
    }
  }, [allItems]);

  // Debounce search term to prevent too many API calls
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (!searchTerm || searchTerm.trim() === '') {
      setDebouncedSearchTerm('');
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Make the API call when the debounced search term changes
  useEffect(() => {
    if (!debouncedSearchTerm) return;
    fetchSearchResults({
      body: getSearchParams(
        buildSearchQuery(debouncedSearchTerm, excludeOldWorkItems)
      ),
    });
  }, [
    debouncedSearchTerm,
    excludeOldWorkItems,
    fetchSearchResults,
    getSearchParams,
    buildSearchQuery,
  ]);

  // Update loading state when search request completes
  useEffect(() => {
    if (searchResultsLoading === false) {
      setSearchLoading(false);
    }
  }, [searchResultsLoading]);

  // Handle activity search - just updates the search term, debounce effect handles the actual API call
  const handleActivitySearch = useCallback(term => {
    setSearchTerm(term);
  }, []);

  return {
    allItems,
    recentItems,
    searchResults,
    searchLoading,
    hasActivities,
    handleActivitySearch,
    allItemsLoading,
    recentItemsLoading,
  };
}
